import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Webhook, Copy, Check, X, Loader2, Plus, Trash2, Activity,
  GitBranch, Clock, ChevronDown, ChevronUp, AlertCircle,
  CheckCircle2, XCircle, Zap, Power, PowerOff
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Provider configuration — labels, placeholders, and setup instructions
const PROVIDER_CONFIG = {
  github: {
    label: 'GitHub',
    color: 'bg-slate-800 text-white',
    urlPlaceholder: 'https://github.com/owner/repository',
    patLabel: 'Personal Access Token',
    patPlaceholder: 'ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
    patHelp: 'Needs "Contents: Read" permission. Create at Settings > Developer settings > Personal access tokens.',
    defaultBranch: 'refs/heads/main',
    instructions: [
      ['Go to your repository on GitHub', null],
      ['Navigate to Settings > Webhooks > Add webhook', null],
      ['Paste the Webhook URL into the "Payload URL" field', null],
      ['Set Content type to application/json', null],
      ['Select "Just the push event"', null],
      ['Click Add webhook', null],
    ],
  },
  azure_devops: {
    label: 'Azure DevOps',
    color: 'bg-blue-600 text-white',
    urlPlaceholder: 'https://dev.azure.com/org/project/_git/repo',
    patLabel: 'Personal Access Token (PAT)',
    patPlaceholder: 'Your Azure DevOps PAT',
    patHelp: 'Needs "Code (Read)" permission. Create at User Settings > Personal access tokens.',
    defaultBranch: 'refs/heads/main',
    instructions: [
      ['Go to Project Settings > Service hooks', null],
      ['Click + Create subscription', null],
      ['Select Web Hooks as the service', null],
      ['Choose Code pushed as the trigger', null],
      ['Set Repository & Branch filters as needed', null],
      ['Paste the Webhook URL into the URL field', null],
      ['Click Test to verify, then Finish', null],
    ],
  },
  bitbucket: {
    label: 'Bitbucket',
    color: 'bg-blue-500 text-white',
    urlPlaceholder: 'https://bitbucket.org/workspace/repository',
    patLabel: 'App Password (username:password)',
    patPlaceholder: 'username:app_password',
    patHelp: 'Create at Personal settings > App passwords. Format: username:app_password. Needs "Repositories: Read".',
    defaultBranch: 'refs/heads/main',
    instructions: [
      ['Go to your repository on Bitbucket', null],
      ['Navigate to Repository settings > Webhooks > Add webhook', null],
      ['Give it a title (e.g., "CloudFactory Docs")', null],
      ['Paste the Webhook URL into the URL field', null],
      ['Under Triggers, select Repository push', null],
      ['Click Save', null],
    ],
  },
  gitlab: {
    label: 'GitLab',
    color: 'bg-orange-600 text-white',
    urlPlaceholder: 'https://gitlab.com/owner/repository',
    patLabel: 'Personal Access Token',
    patPlaceholder: 'glpat-xxxxxxxxxxxxxxxxxxxx',
    patHelp: 'Needs "read_repository" and "api" scopes. Create at User Settings > Access Tokens.',
    defaultBranch: 'refs/heads/main',
    instructions: [
      ['Go to your project on GitLab', null],
      ['Navigate to Settings > Webhooks', null],
      ['Paste the Webhook URL into the URL field', null],
      ['Under Trigger, check Push events', null],
      ['Optionally filter by branch (e.g., main)', null],
      ['Click Add webhook', null],
    ],
  },
};

const PROVIDERS = Object.keys(PROVIDER_CONFIG);

const WebhookPanel = ({ projectId, documentation, isOpen, onClose }) => {
  const { canEdit, canDelete } = useAuth();
  const [configs, setConfigs] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('configs');
  const [expandedConfig, setExpandedConfig] = useState(null);
  const [copied, setCopied] = useState(null);
  const [credentialsByConfig, setCredentialsByConfig] = useState({});
  const [credentialLoading, setCredentialLoading] = useState({});

  // Form state
  const [formDocId, setFormDocId] = useState('');
  const [formRepoUrl, setFormRepoUrl] = useState('');
  const [formPatToken, setFormPatToken] = useState('');
  const [formBranch, setFormBranch] = useState('refs/heads/main');
  const [formProvider, setFormProvider] = useState('github');

  useEffect(() => {
    if (isOpen) fetchData();
  }, [isOpen, projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configsRes, activitiesRes] = await Promise.all([
        axios.get(`${API_URL}/webhooks/config?project_id=${projectId}`),
        axios.get(`${API_URL}/webhooks/activity?project_id=${projectId}&limit=20`),
      ]);
      setConfigs(configsRes.data);
      setActivities(activitiesRes.data);
    } catch (e) {
      console.error('Failed to fetch webhook data:', e);
    } finally {
      setLoading(false);

  const fetchCredentials = async (configId, forceRefresh = false) => {
    if (!forceRefresh && credentialsByConfig[configId]) return credentialsByConfig[configId];
    setCredentialLoading((prev) => ({ ...prev, [configId]: true }));
    try {
      const res = await axios.get(`${API_URL}/webhooks/config/${configId}/credentials`);
      setCredentialsByConfig((prev) => ({ ...prev, [configId]: res.data }));
      return res.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load webhook credentials');
      return null;
    } finally {
      setCredentialLoading((prev) => ({ ...prev, [configId]: false }));
    }
  };

  const handleRotateSecret = async (configId) => {
    try {
      const res = await axios.post(`${API_URL}/webhooks/config/${configId}/rotate-secret`);
      setCredentialsByConfig((prev) => ({ ...prev, [configId]: res.data }));
      setConfigs((prev) => prev.map((cfg) => (
        cfg.id === configId
          ? {
              ...cfg,
              verification_status: res.data.verification_status,
              verification_method: res.data.verification_method,
              verification_header: res.data.verification_header,
              verification_username: res.data.verification_username,
              verification_secret_hint: res.data.verification_secret_hint,
              secret_last_rotated_at: res.data.secret_last_rotated_at,
            }
          : cfg
      )));
      toast.success('Webhook secret rotated. Update the provider configuration now.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rotate webhook secret');
    }
  };

    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formDocId || !formRepoUrl || !formPatToken) {
      toast.error('Please fill in all required fields');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/webhooks/config`, {
        project_id: projectId,
        doc_id: formDocId,
        repo_url: formRepoUrl,
        pat_token: formPatToken,
        branch_filter: formBranch,
        provider: formProvider,
      });
      setCredentialsByConfig((prev) => ({ ...prev, [res.data.id]: res.data }));
      toast.success('Webhook created! Copy the webhook secret into your provider settings.');
      setShowCreateForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create webhook config');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setFormDocId('');
    setFormRepoUrl('');
    setFormPatToken('');
    setFormBranch('refs/heads/main');
    setFormProvider('github');
  };

  const handleDelete = async (configId) => {
    if (!window.confirm('Delete this webhook configuration?')) return;
    try {
      await axios.delete(`${API_URL}/webhooks/config/${configId}`);
      toast.success('Webhook config deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete webhook config');
    }
  };

  const handleToggle = async (configId) => {
    try {
      const res = await axios.patch(`${API_URL}/webhooks/config/${configId}/toggle`);
      toast.success(res.data.is_active ? 'Webhook enabled' : 'Webhook disabled');
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle webhook');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    toast.success(`${label} copied to clipboard`);
    setTimeout(() => setCopied(null), 2000);
  };

  const getWebhookUrl = (token) => `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/receive/${token}`;

  const statusIcon = {
    processing: <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />,
    completed: <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />,
    failed: <XCircle className="h-3.5 w-3.5 text-red-500" />,
  };

  const statusColor = {
    processing: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
  };

  const currentProviderConfig = PROVIDER_CONFIG[formProvider] || PROVIDER_CONFIG.github;

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-0 bottom-0 w-full sm:w-[520px] bg-white border-l border-slate-200 shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-white">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center">
            <Webhook className="h-4 w-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900" data-testid="webhook-panel-title">Auto-Sync Webhooks</h3>
            <p className="text-[10px] text-slate-500">GitHub, Azure DevOps, Bitbucket, GitLab</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0" data-testid="webhook-close-btn">
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-4 py-2 border-b border-slate-100 bg-slate-50">
        <button
          onClick={() => setActiveTab('configs')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${activeTab === 'configs' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
          data-testid="webhook-tab-configs"
        >
          <Zap className="h-3 w-3" /> Webhooks ({configs.length})
        </button>
        <button
          onClick={() => setActiveTab('activity')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${activeTab === 'activity' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
          data-testid="webhook-tab-activity"
        >
          <Activity className="h-3 w-3" /> Activity ({activities.length})
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
          </div>
        ) : activeTab === 'configs' ? (
          <div className="p-4 space-y-3">
            {/* Create button */}
            {canEdit && !showCreateForm && (
              <Button onClick={() => setShowCreateForm(true)} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" size="sm" data-testid="webhook-add-btn">
                <Plus className="h-4 w-4 mr-2" />Add Webhook
              </Button>
            )}

            {/* Create form */}
            {showCreateForm && (
              <Card className="border-emerald-200 bg-emerald-50/30">
                <CardContent className="p-4">
                  <h4 className="text-sm font-semibold text-slate-900 mb-3">New Webhook Configuration</h4>
                  <form onSubmit={handleCreate} className="space-y-3">
                    {/* Provider selector */}
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1.5">Git Provider *</label>
                      <div className="flex gap-1.5" data-testid="webhook-provider-selector">
                        {PROVIDERS.map((p) => (
                          <button
                            key={p}
                            type="button"
                            onClick={() => {
                              setFormProvider(p);
                              setFormBranch(PROVIDER_CONFIG[p].defaultBranch);
                            }}
                            className={`flex-1 text-xs py-2 px-2 rounded-md font-medium transition-all border ${
                              formProvider === p
                                ? 'border-emerald-500 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-500/20'
                                : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                            }`}
                            data-testid={`webhook-provider-${p}`}
                          >
                            {PROVIDER_CONFIG[p].label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Document */}
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">Document to auto-update *</label>
                      <select
                        value={formDocId}
                        onChange={(e) => setFormDocId(e.target.value)}
                        className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        required
                        data-testid="webhook-doc-select"
                      >
                        <option value="">Select documentation...</option>
                        {documentation.map(doc => (
                          <option key={doc.id} value={doc.id}>{doc.title} (v{doc.version})</option>
                        ))}
                      </select>
                    </div>

                    {/* Repo URL */}
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">Repository URL *</label>
                      <input
                        type="text"
                        value={formRepoUrl}
                        onChange={(e) => setFormRepoUrl(e.target.value)}
                        placeholder={currentProviderConfig.urlPlaceholder}
                        className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        required
                        data-testid="webhook-repo-url"
                      />
                    </div>

                    {/* PAT */}
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">{currentProviderConfig.patLabel} *</label>
                      <input
                        type="password"
                        value={formPatToken}
                        onChange={(e) => setFormPatToken(e.target.value)}
                        placeholder={currentProviderConfig.patPlaceholder}
                        className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        required
                        data-testid="webhook-pat-input"
                      />
                      <p className="text-[10px] text-slate-400 mt-0.5">{currentProviderConfig.patHelp}</p>
                    </div>

                    {/* Branch */}
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">Branch to watch</label>
                      <input
                        type="text"
                        value={formBranch}
                        onChange={(e) => setFormBranch(e.target.value)}
                        placeholder="refs/heads/main"
                        className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        data-testid="webhook-branch-input"
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button type="submit" disabled={creating} className="flex-1 bg-emerald-600 hover:bg-emerald-700" size="sm" data-testid="webhook-create-submit">
                        {creating ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />}
                        {creating ? 'Creating...' : 'Create Webhook'}
                      </Button>
                      <Button type="button" variant="outline" onClick={() => { setShowCreateForm(false); resetForm(); }} size="sm" data-testid="webhook-create-cancel">
                        Cancel
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Config list */}
            {configs.map((config) => {
              const providerInfo = PROVIDER_CONFIG[config.provider] || PROVIDER_CONFIG.github;
              const credentials = credentialsByConfig[config.id] || null;
              const verificationStatus = credentials?.verification_status || config.verification_status || 'legacy_url_only';
              return (
                <Card key={config.id} className={`border-slate-200 ${!config.is_active ? 'opacity-60' : ''}`} data-testid={`webhook-config-${config.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Badge className={`${providerInfo.color} text-[10px] shrink-0`}>{providerInfo.label}</Badge>
                        <span className="text-sm font-medium text-slate-900 truncate">{config.doc_title}</span>
                        <Badge variant={config.is_active ? 'default' : 'secondary'}
                          className={config.is_active ? 'bg-emerald-100 text-emerald-700 text-[10px]' : 'text-[10px]'}>
                          {config.is_active ? 'Active' : 'Disabled'}
                        </Badge>
                        <Badge className={`text-[10px] ${verificationStatus === 'provider_verified' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                          {verificationStatus === 'provider_verified' ? 'Verified' : 'Legacy URL-only'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {canEdit && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                            onClick={() => handleToggle(config.id)}
                            title={config.is_active ? 'Disable' : 'Enable'}
                            data-testid={`webhook-toggle-${config.id}`}>
                            {config.is_active ? <PowerOff className="h-3.5 w-3.5 text-slate-400" /> : <Power className="h-3.5 w-3.5 text-emerald-500" />}
                          </Button>
                        )}
                        {canDelete && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
                            onClick={() => handleDelete(config.id)}
                            data-testid={`webhook-delete-${config.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="text-xs text-slate-500 space-y-1">
                      <div className="flex items-center gap-1.5 truncate">
                        <GitBranch className="h-3 w-3 shrink-0" />
                        <span className="truncate">{config.repo_url}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <GitBranch className="h-3 w-3 shrink-0" />
                        <span>{config.branch_filter}</span>
                      </div>
                      {config.last_triggered_at && (
                        <div className="flex items-center gap-1.5">
                          <Clock className="h-3 w-3 shrink-0" />
                          <span>Last triggered: {new Date(config.last_triggered_at).toLocaleString()}</span>
                        </div>
                      )}
                    </div>

                    {/* Expand/collapse setup instructions */}
                    <button
                      onClick={async () => {
                        const nextExpanded = expandedConfig === config.id ? null : config.id;
                        setExpandedConfig(nextExpanded);
                        if (nextExpanded === config.id) {
                          await fetchCredentials(config.id);
                        }
                      }}
                      className="mt-2 text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1 font-medium"
                      data-testid={`webhook-instructions-toggle-${config.id}`}
                    >
                      {expandedConfig === config.id ? 'Hide' : 'Show'} Setup Instructions
                      {expandedConfig === config.id ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </button>

                    {expandedConfig === config.id && (
                      <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs space-y-3">
                        <div>
                          <p className="font-semibold text-slate-700 mb-1">1. Copy this Webhook URL:</p>
                          <div className="flex items-center gap-1.5 bg-white p-2 rounded border border-slate-200">
                            <code className="text-[11px] text-slate-600 flex-1 break-all">
                              {getWebhookUrl(config.webhook_token)}
                            </code>
                            <button
                              onClick={() => copyToClipboard(getWebhookUrl(config.webhook_token), `url-${config.id}`)}
                              className="shrink-0 p-1 hover:bg-slate-100 rounded"
                              data-testid={`webhook-copy-url-${config.id}`}
                            >
                              {copied === `url-${config.id}` ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5 text-slate-400" />}
                            </button>
                          </div>
                        </div>

                        <div>
                          <p className="font-semibold text-slate-700 mb-1">2. In {providerInfo.label}:</p>
                          <ol className="list-decimal list-inside text-slate-600 space-y-1">
                            {providerInfo.instructions.map(([step], idx) => (
                              <li key={`instr-${idx}-${step.slice(0, 20)}`}>{step}</li>
                            ))}
                          </ol>
                        </div>

                        <div className="bg-white rounded border border-slate-200 p-3 space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <p className="font-semibold text-slate-700">3. Provider verification credentials</p>
                              <p className="text-[10px] text-slate-500">
                                {verificationStatus === 'provider_verified'
                                  ? 'Configure these values in your provider so requests are verified server-side.'
                                  : 'This webhook still accepts URL-only verification. Rotate and configure the provider secret to harden it.'}
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleRotateSecret(config.id)}
                              className="h-7 text-[10px]"
                            >
                              Rotate Secret
                            </Button>
                          </div>

                          {credentialLoading[config.id] ? (
                            <div className="flex items-center gap-2 text-slate-500">
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              <span>Loading credentials…</span>
                            </div>
                          ) : credentials ? (
                            <>
                              <div className="text-[10px] text-slate-500">
                                Method: <span className="font-medium text-slate-700">{credentials.verification_method}</span>
                                {' · '}
                                Header: <span className="font-medium text-slate-700">{credentials.verification_header}</span>
                              </div>

                              {config.provider === 'azure_devops' ? (
                                <div className="space-y-2">
                                  <div>
                                    <p className="text-[10px] font-medium text-slate-600 mb-1">Basic auth username</p>
                                    <div className="flex items-center gap-1.5 bg-slate-50 p-2 rounded border border-slate-200">
                                      <code className="text-[11px] text-slate-700 flex-1 break-all">{credentials.verification_username}</code>
                                      <button
                                        onClick={() => copyToClipboard(credentials.verification_username, `azure-user-${config.id}`)}
                                        className="shrink-0 p-1 hover:bg-slate-100 rounded"
                                      >
                                        {copied === `azure-user-${config.id}` ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5 text-slate-400" />}
                                      </button>
                                    </div>
                                  </div>
                                  <div>
                                    <p className="text-[10px] font-medium text-slate-600 mb-1">Basic auth password</p>
                                    <div className="flex items-center gap-1.5 bg-slate-50 p-2 rounded border border-slate-200">
                                      <code className="text-[11px] text-slate-700 flex-1 break-all">{credentials.verification_secret}</code>
                                      <button
                                        onClick={() => copyToClipboard(credentials.verification_secret, `azure-pass-${config.id}`)}
                                        className="shrink-0 p-1 hover:bg-slate-100 rounded"
                                      >
                                        {copied === `azure-pass-${config.id}` ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5 text-slate-400" />}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <p className="text-[10px] font-medium text-slate-600 mb-1">
                                    {config.provider === 'gitlab' ? 'Secret token' : 'Webhook secret'}
                                  </p>
                                  <div className="flex items-center gap-1.5 bg-slate-50 p-2 rounded border border-slate-200">
                                    <code className="text-[11px] text-slate-700 flex-1 break-all">{credentials.verification_secret}</code>
                                    <button
                                      onClick={() => copyToClipboard(credentials.verification_secret, `secret-${config.id}`)}
                                      className="shrink-0 p-1 hover:bg-slate-100 rounded"
                                      data-testid={`webhook-copy-secret-${config.id}`}
                                    >
                                      {copied === `secret-${config.id}` ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5 text-slate-400" />}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </>
                          ) : (
                            <p className="text-[10px] text-slate-500">Credentials not loaded yet.</p>
                          )}
                        </div>

                        <div className="flex items-start gap-1.5 text-amber-600 bg-amber-50 p-2 rounded">
                          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                          <p>
                            The URL token now identifies the webhook config, but provider-native verification is the primary auth layer.
                            {verificationStatus !== 'provider_verified' && ' Rotate the secret and update the provider to complete hardening.'}
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {configs.length === 0 && !showCreateForm && (
              <div className="text-center py-8">
                <Webhook className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500 mb-1">No webhooks configured</p>
                <p className="text-xs text-slate-400">Set up a webhook to auto-update docs when code is pushed</p>
              </div>
            )}
          </div>
        ) : (
          /* Activity tab */
          <div className="p-4 space-y-2">
            {activities.length === 0 ? (
              <div className="text-center py-8">
                <Activity className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No webhook activity yet</p>
              </div>
            ) : (
              activities.map((act) => {
                const providerInfo = PROVIDER_CONFIG[act.provider] || null;
                return (
                  <Card key={act.id} className="border-slate-200" data-testid={`webhook-activity-${act.id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          {statusIcon[act.status]}
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusColor[act.status]}`}>
                            {act.status}
                          </span>
                          {providerInfo && (
                            <Badge className={`${providerInfo.color} text-[9px] px-1.5 py-0`}>{providerInfo.label}</Badge>
                          )}
                        </div>
                        <span className="text-[10px] text-slate-400">
                          {new Date(act.created_at).toLocaleString()}
                        </span>
                      </div>
                      {act.result_message && (
                        <p className="text-xs text-slate-600 mb-1.5">{act.result_message}</p>
                      )}
                      {act.commits && act.commits.length > 0 && (
                        <div className="text-[10px] text-slate-500 space-y-0.5">
                          {act.commits.slice(0, 3).map((c, i) => (
                            <div key={c.id || `commit-${i}`} className="flex items-center gap-1.5">
                              <GitBranch className="h-2.5 w-2.5" />
                              <span className="truncate">{c.message || c.id?.slice(0, 8)}</span>
                              <span className="text-slate-400">by {c.author}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {act.changed_files && act.changed_files.length > 0 && (
                        <div className="mt-1.5 text-[10px] text-slate-400">
                          Files: {act.changed_files.slice(0, 5).join(', ')}
                          {act.changed_files.length > 5 && ` +${act.changed_files.length - 5} more`}
                        </div>
                      )}
                      {act.doc_version_before && act.doc_version_after && (
                        <div className="mt-1 text-[10px] font-medium text-emerald-600">
                          Doc updated: v{act.doc_version_before} &rarr; v{act.doc_version_after}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WebhookPanel;
